import React, { useMemo } from 'react';
import { useGPS } from '../../contexts/GPSContext';

const SCORE_MAX = 5;

// The card reads the same sensed array as GPSRequestTypeChart, so it must say
// which population it is scoring. Under "en cours" the answer is structurally
// none: a rating is solicited at closure.
const SUBTITLES = {
  all: 'Encours à fin de période',
  ongoing: 'Encours à fin de période',
  received: 'Tickets reçus sur la période',
  resolved: 'Tickets fermés sur la période',
};

// Gauge thresholds: green above 3, red at 2 and below, amber in between.
const GOOD_SCORE = 3;
const POOR_SCORE = 2;

// null guarded first: it coerces to 0 and would otherwise read as "poor".
const gaugeColor = (average) => {
  if (average === null) return 'bg-[#f5a623]';
  if (average > GOOD_SCORE) return 'bg-[#16a866]';
  if (average <= POOR_SCORE) return 'bg-[#e5484d]';
  return 'bg-[#f5a623]';
};

// Snowflake AVG can serialise as a string; anything non-finite is "not rated".
const toScore = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatScore = (value) => value.toFixed(1).replace('.', ',');

const averageOf = ({ rated, total }) => (rated > 0 ? total / rated : null);

// Rated types first, best average on top; unrated ones keep a stable order by
// solicitation volume so the card does not reshuffle between renders.
const byScoreThenVolume = (a, b) => {
  const left = averageOf(a);
  const right = averageOf(b);
  if (left === null && right === null) return b.requested - a.requested;
  if (left === null) return 1;
  if (right === null) return -1;
  return right - left || b.rated - a.rated;
};

// Single pass: per-type groups and the card-level totals come out together, so the
// global average is never rebuilt by multiplying per-type averages back out.
const summarise = (issues) => {
  const groups = new Map();
  let rated = 0;
  let total = 0;

  issues.forEach((issue) => {
    if (!issue?.rating_requested) return;
    const label = issue.request_type || issue.requestType || 'Non spécifié';
    const group = groups.get(label) || { label, requested: 0, rated: 0, total: 0 };
    group.requested += 1;
    const score = toScore(issue.rating_score);
    if (score !== null) {
      group.rated += 1;
      group.total += score;
      rated += 1;
      total += score;
    }
    groups.set(label, group);
  });

  return {
    rows: Array.from(groups.values()).sort(byScoreThenVolume),
    rated,
    average: rated > 0 ? total / rated : null,
  };
};

const GPSInterventionRatingsChart = ({ sense = 'all' }) => {
  const gps = useGPS();

  const { rows, rated, average } = useMemo(
    () => summarise(Array.isArray(gps?.summarySensedIssues) ? gps.summarySensedIssues : []),
    [gps?.summarySensedIssues]
  );

  const cardClass = 'bg-white dark:bg-[#1e1e1e] p-5 sm:p-6 rounded-2xl border border-gray-200 dark:border-gray-900 h-full flex flex-col';

  const header = (
    <div className="flex items-center gap-3 mb-5">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#fdf6e7] dark:bg-[#3a3120] text-[#f5a623] flex-none">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </span>
      <div className="min-w-0">
        <span className="block text-base font-bold text-gray-900 dark:text-white">Satisfaction par type d'intervention</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">
          {rated > 0
            ? `${SUBTITLES[sense] || SUBTITLES.all} · ${formatScore(average)} / ${SCORE_MAX} sur ${rated} avis`
            : `${SUBTITLES[sense] || SUBTITLES.all} · notes données après intervention`}
        </span>
      </div>
    </div>
  );

  if (rows.length === 0) {
    return (
      <div className={cardClass}>
        {header}
        <p className="flex-1 flex items-center justify-center text-center text-xs text-gray-600 dark:text-gray-400">
          {sense === 'ongoing' || sense === 'all'
            ? 'Aucune note sur les tickets encore ouverts : elle est demandée à la clôture.'
            : 'Aucune intervention sollicitée pour une note sur la période.'}
        </p>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      {header}

      {/* One line per type, so the two headers genuinely sit above their columns.
          The label truncates with a title, the pattern GPSTopGraph already uses. */}
      <div className="flex items-baseline gap-3 pb-2 border-b border-gray-100 dark:border-gray-800 text-[10px] text-gray-400 dark:text-gray-500">
        <span className="flex-1 min-w-0" />
        <span className="w-16 sm:w-24 flex-none" />
        <span className="w-14 flex-none text-right">Note</span>
        <span className="w-16 flex-none text-right">Avis</span>
      </div>

      <div className="flex-1 min-h-0 flex flex-col divide-y divide-gray-100 dark:divide-gray-800 overflow-auto">
        {rows.map((row) => {
          const rowAverage = averageOf(row);
          // Clamped: SCORE_MAX is an assumption about the source scale, and an
          // out-of-range average must not spill the bar over the next columns.
          const pct = rowAverage === null
            ? 0
            : Math.min(100, Math.max(0, Math.round((rowAverage / SCORE_MAX) * 100)));
          return (
            <div key={row.label} className="flex items-center gap-3 py-2.5">
              <span
                className="flex-1 min-w-0 truncate text-xs text-gray-800 dark:text-gray-200"
                title={row.label}
              >
                {row.label}
              </span>
              {/* An unrated type is indeterminate, not a zero: no aria-valuenow. */}
              <span className="w-16 sm:w-24 flex-none bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <span
                  className={`block h-2 rounded-full ${gaugeColor(rowAverage)}`}
                  style={{ width: `${pct}%` }}
                  role="progressbar"
                  aria-valuenow={rowAverage === null ? undefined : rowAverage}
                  aria-valuemin={0}
                  aria-valuemax={SCORE_MAX}
                  aria-valuetext={rowAverage === null ? 'non noté' : `${formatScore(rowAverage)} sur ${SCORE_MAX}`}
                  aria-label={`Note moyenne ${row.label}`}
                />
              </span>
              <span className="w-14 flex-none text-right tabular-nums text-xs font-semibold text-gray-900 dark:text-white">
                {rowAverage === null ? '—' : `${formatScore(rowAverage)} / ${SCORE_MAX}`}
              </span>
              {/* Answers over solicitations: the sample size the average rests on,
                  so a 5,0 from one review cannot read like a 5,0 from forty. */}
              <span
                className="w-16 flex-none text-right tabular-nums text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap"
                title={`${row.rated} avis sur ${row.requested} sollicitations`}
              >
                {`${row.rated} / ${row.requested}`}
              </span>
            </div>
          );
        })}
      </div>

      {rated === 0 && (
        // The total now sits in every row's "x / y", so the footer states only what
        // the columns cannot: nobody has answered yet.
        <p className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-[10px] text-gray-500 dark:text-gray-400">
          Aucune réponse à ce jour.
        </p>
      )}
    </div>
  );
};

export default GPSInterventionRatingsChart;
